/**
 * Blogs.js  —  src/pages/Blogs.js
 *
 * PERFORMANCE NOTE — The 50-second cold load is Render's free tier
 * spinning up after 15 min of inactivity. Two things help:
 *   1. sessionStorage cache (implemented below)
 *   2. Keep-alive ping via UptimeRobot every 14 min
 *      → monitor: ${API_URL}/api/health
 */

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { FiCalendar, FiUser, FiArrowRight, FiTag } from "react-icons/fi";
import HomeIcon from "@mui/icons-material/Home";
import FolderIcon from "@mui/icons-material/Folder";
import "../styles/Blogs.css";

const API_URL   = process.env.REACT_APP_API_URL;
const CACHE_KEY = "blogs_session_cache";

/* ── Cache helpers ── */
function getCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function setCache(data) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Skeleton card ── */
function SkeletonCard() {
  return (
    <div className="blog-card blog-card--skeleton">
      <div className="blog-card-image skeleton-block" />
      <div className="blog-card-body">
        <div className="skeleton-line w40" />
        <div className="skeleton-line w80 lg" />
        <div className="skeleton-line w60" />
        <div className="skeleton-line w90" />
        <div className="skeleton-line w70" />
      </div>
    </div>
  );
}

/* ── Component ── */
function Blogs() {
  const [blogs,   setBlogs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const cached = getCache();
    if (cached) { setBlogs(cached); setLoading(false); return; }

    fetch(`${API_URL}/api/blogs`)
      .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
      .then((data) => { setBlogs(data); setCache(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });

  return (
    <div className="blogs-page">
      <Helmet>
        <title>Blog | Biko — Cyber Security &amp; Engineering Insights</title>
        <meta
          name="description"
          content="Articles and write-ups by Biko on MERN Stack development, Cyber Security, DevSecOps, penetration testing and software engineering."
        />
        <meta property="og:title" content="Blog | Biko" />
        <meta property="og:url"   content="https://bikosulu.com/blogs" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type":    "Blog",
              name:       "Biko Blog",
              url:        "https://bikosulu.com/blogs",
              author: { "@type": "Person", name: "Biko Sulu" },
            }),
          }}
        />
      </Helmet>

      <div className="blogs-content">

        {/* ── Page navigation toggle ── */}
        <nav className="page-nav-toggle">
          <button className="page-nav-btn" onClick={() => navigate("/")}>
            <HomeIcon style={{ fontSize: 15 }} />
            Home
          </button>
          <span className="page-nav-sep">/</span>
          <button className="page-nav-btn" onClick={() => navigate("/projects")}>
            <FolderIcon style={{ fontSize: 15 }} />
            Projects
          </button>
          <span className="page-nav-sep">/</span>
          <span className="page-nav-current">Blog</span>
        </nav>

        {/* ── Header ── */}
        <span className="blogs-eyebrow">Writing</span>
        <h1 className="blogs-title">
          Field <span>Notes</span>
        </h1>
        <p className="blogs-subtitle">
          Insights on security engineering, DevSecOps, and building production systems.
        </p>

        {loading ? (
          <div className="blog-grid">
            {[1, 2, 3].map((n) => <SkeletonCard key={n} />)}
          </div>
        ) : blogs.length === 0 ? (
          <p className="no-blogs">No posts yet. Check back soon.</p>
        ) : (
          <div className="blog-grid">
            {blogs.map((blog) => (
              <article
                key={blog._id}
                className="blog-card"
                onClick={() => navigate(`/blogs/${blog._id}`)}
                tabIndex={0}
                role="button"
                aria-label={`Read ${blog.title}`}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/blogs/${blog._id}`)}
              >
                {/* Image */}
                <div className="blog-card-image">
                  {blog.image ? (
                    <img src={blog.image} alt={blog.title} loading="lazy" />
                  ) : (
                    <div className="blog-card-placeholder">
                      <span className="placeholder-mono">BIKO<b>.</b></span>
                      <div className="blog-placeholder-grid" />
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div className="blog-img-overlay">
                    <span className="blog-overlay-cta">
                      Read Article <FiArrowRight size={14} />
                    </span>
                  </div>
                </div>

                {/* Body */}
                <div className="blog-card-body">
                  <div className="blog-meta-row">
                    <span className="blog-tag-pill">
                      <FiTag size={10} /> Blog
                    </span>
                    <span className="blog-date">
                      <FiCalendar size={11} /> {formatDate(blog.createdAt)}
                    </span>
                  </div>

                  <h2 className="blog-card-title">{blog.title}</h2>

                  {blog.author && (
                    <span className="blog-author">
                      <FiUser size={11} /> {blog.author}
                    </span>
                  )}

                  <p className="blog-card-summary">{blog.summary}</p>

                  <button
                    className="blog-read-btn"
                    onClick={(e) => { e.stopPropagation(); navigate(`/blogs/${blog._id}`); }}
                  >
                    Read more <FiArrowRight size={13} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Blogs;